export const ALLOWED_HOST_PATTERN =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?=[a-z])[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$(?!\n)/u

const SCHEME_PREFIX = /^[a-z][a-z0-9+.-]*:\/\//u
const PORT_SUFFIX = /:\d+$/u
const WILDCARD_PREFIX = "*."

export function normalizeHostEntry(entry: string): string {
  const withoutScheme = entry.trim().toLowerCase().replace(SCHEME_PREFIX, "")
  const [authority = ""] = withoutScheme.split(/[/?#]/u)
  const withoutPort = authority.replace(PORT_SUFFIX, "")
  return withoutPort.startsWith(WILDCARD_PREFIX)
    ? withoutPort.slice(WILDCARD_PREFIX.length)
    : withoutPort
}

export interface HostEntryMessage {
  kind: "invalid" | "skipped"
  text: string
}

export interface HostEntriesResult {
  added: string[]
  messages: HostEntryMessage[]
  invalid: string[]
}

interface CurrentHosts {
  allowedHosts: string[]
  lockedHosts: string[]
}

// Locked and allowed entries both cover their subdomains, exact deployment hosts included.
export function isHostCovered(host: string, current: CurrentHosts): boolean {
  return [...current.lockedHosts, ...current.allowedHosts].some(
    (parent) => host === parent || host.endsWith(`.${parent}`),
  )
}

export function parseHostEntries(input: string, current: CurrentHosts): HostEntriesResult {
  const added: string[] = []
  const messages: HostEntryMessage[] = []
  const invalid: string[] = []

  const entries = input
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry !== "")

  for (const entry of entries) {
    const host = normalizeHostEntry(entry)
    if (!ALLOWED_HOST_PATTERN.test(host)) {
      messages.push({ kind: "invalid", text: `Not a valid domain: ${entry}` })
      invalid.push(entry)
      continue
    }
    if (isHostCovered(host, { ...current, allowedHosts: [...current.allowedHosts, ...added] })) {
      messages.push({ kind: "skipped", text: `${entry} is already allowed.` })
      continue
    }
    added.push(host)
  }

  return { added, messages, invalid }
}
